/* Reusable OTPless phone-verification helper.
 *
 * Wraps the OTPless Headless SDK behind a small, framework-agnostic API so any
 * form or page can add phone OTP without re-implementing SDK loading, the
 * singleton signin object, or the single-callback event routing.
 *
 *   var otp = FridoOtpless.create(appId, {
 *     onSent:     function ()      {},  // OTP dispatched — show the OTP input
 *     onAutoRead: function (code)  {},  // SMS auto-read — prefill the OTP
 *     onVerified: function (token) {},  // success — token proves the phone
 *     onError:    function (msg)   {},  // any failure, human-readable message
 *   });
 *   otp.sendOtp(phone, countryCode);       // e.g. ("9876543210", "+91")
 *   otp.verifyOtp(phone, otp, countryCode);
 *
 * Only the App ID (public) is used here. The Client ID / Secret are backend-only
 * and must never reach the browser — the returned token is validated server-side.
 *
 * SECURITY NOTE: a token from onVerified proves the phone ONLY once your backend
 * validates it with OTPless. Never treat client-side success as trusted on its own.
 */
(function () {
  'use strict';

  var SDK_SRC = 'https://otpless.com/v4/headless.js';
  var sdkPromise = null;

  /* Load the headless SDK once per page, regardless of how many forms use it. */
  function loadSdk(appId) {
    if (sdkPromise) return sdkPromise;

    sdkPromise = new Promise(function (resolve, reject) {
      if (window.OTPless) return resolve(window.OTPless);

      var script = document.getElementById('otpless-sdk') || document.createElement('script');
      var isNew = !script.parentNode;
      script.id = 'otpless-sdk';
      script.src = SDK_SRC;
      script.setAttribute('data-appid', appId);

      script.addEventListener('load', function () {
        if (window.OTPless) resolve(window.OTPless);
        else reject(new Error('OTPless SDK loaded but the OTPless global is missing'));
      });
      script.addEventListener('error', function () {
        sdkPromise = null; // allow a later retry
        reject(new Error('Failed to load the OTPless SDK'));
      });

      if (isNew) document.head.appendChild(script);
    });

    return sdkPromise;
  }

  function create(appId, handlers) {
    if (!appId) throw new Error('FridoOtpless.create: appId is required');
    handlers = handlers || {};

    var signin = null;

    function fire(name, arg) {
      if (typeof handlers[name] === 'function') handlers[name](arg);
    }

    /* The SDK funnels every stage — INITIATE, OTP_AUTO_READ, VERIFY, ONETAP,
       FAILED — through this one callback. Different SDK builds label the final
       success ONETAP or VERIFY, so both are accepted and the token is read
       defensively. */
    function onEvent(event) {
      if (!event) return;
      var type = event.responseType;
      var status = event.statusCode;
      var response = event.response || {};

      if (type === 'OTP_AUTO_READ') {
        var code = response.otp || (response.data && response.data.otp);
        if (code) fire('onAutoRead', String(code));
        return;
      }

      if (type === 'FAILED' || (status && status !== 200)) {
        fire('onError', response.errorMessage || response.message || 'Verification failed. Please try again.');
        return;
      }

      if (type === 'INITIATE') {
        fire('onSent');
        return;
      }

      if (type === 'ONETAP' || type === 'VERIFY') {
        if (response.verified === false || response.isOTPVerified === false) {
          fire('onError', 'Incorrect OTP. Please try again.');
          return;
        }
        var token = response.token || (response.data && response.data.token) || '';
        /* A VERIFY with no token is a rejected OTP, not a success — some builds
           signal a wrong code that way rather than with verified:false. Treating
           it as success would mark an unverified phone as verified. */
        if (!token && type === 'VERIFY') {
          fire('onError', 'Incorrect OTP. Please try again.');
          return;
        }
        fire('onVerified', token);
      }

      /* Any other responseType is left alone on purpose — the SDK emits
         lifecycle events we don't model, and erroring on those would flash a
         failure during a perfectly good flow. The caller's verify watchdog is
         what covers an outcome that never arrives. */
    }

    function ensureSignin() {
      return loadSdk(appId).then(function (OTPless) {
        if (!signin) signin = new OTPless(onEvent);
        return signin;
      });
    }

    return {
      sendOtp: function (phone, countryCode) {
        return ensureSignin().then(function (s) {
          s.initiate({ channel: 'PHONE', phone: String(phone), countryCode: countryCode || '+91' });
        }).catch(function (error) {
          fire('onError', 'Could not start verification. Please try again.');
          throw error;
        });
      },
      verifyOtp: function (phone, otp, countryCode) {
        return ensureSignin().then(function (s) {
          s.verify({ channel: 'PHONE', phone: String(phone), otp: String(otp), countryCode: countryCode || '+91' });
        }).catch(function (error) {
          fire('onError', 'Verification failed. Please try again.');
          throw error;
        });
      }
    };
  }

  window.FridoOtpless = { create: create, loadSdk: loadSdk };
})();
